<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { accountService } from '@exchange/libs/account/service/src';
import { marketService } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { subaccountsService } from '@exchange/libs/trading-accounts/service/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';

const props = defineProps({
  marketId: { type: String, required: true },
});

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();

const market = computed(() => marketService.getMarket(props.marketId));
const isPerpetualMarket = computed(() => market.value?.type === MarketType.PERP);

const { accountId } = accountService;
const { currencies } = currencyService;

const mainAccount = computed(() => subaccountsService.list.value.find((account) => account.name === CONSTANTS.MAIN_SUBACCOUNT_NAME));
const mainAccountBalance = computed(() => {
  if (!mainAccount.value || !currencies.value) return 0;
  return mainAccount.value.getBalanceTotal(currencies.value).all;
});
const hasPositiveMainBalance = computed(() => {
  if (!mainAccountBalance.value) return false;
  return !mainAccountBalance.value.isZero?.();
});

const currentSubaccount = computed(() => subaccountsService.getAccountById(accountId.value));
const currentAccountBalance = computed(() => {
  if (!currentSubaccount.value || !currencies.value) return 0;
  return currentSubaccount.value.getBalanceTotal(currencies.value).all;
});
const hasZeroBalance = computed(() => {
  if (!currentAccountBalance.value) return true;
  return currentAccountBalance.value.isZero?.() || currentAccountBalance.value.eq(0);
});

const handleTransferClick = () => {
  router.push({
    ...router.currentRoute.value,
    hash: '#Transfer',
    query: {
      source: CONSTANTS.MAIN_SUBACCOUNT_NAME,
      destination: currentSubaccount.value?.name,
      currency: CONSTANTS.DEFAULT_CURRENCIES.fiat,
    },
  });
};
</script>

<template>
  <div>
    <div
      v-if="isPerpetualMarket && hasZeroBalance && hasPositiveMainBalance"
      class="transfer-panel"
    >
      <div
        class="transfer-panel__notice"
        @click="handleTransferClick"
      >
        {{
          t('warnings.transactions.subaccountTransfer.transferPanel.notice', {
            currentBalance: currentAccountBalance,
            mainBalance: mainAccountBalance,
          })
        }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.transfer-panel {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-elevation-2));
  margin-bottom: 12px;
  background: rgba(var(--v-theme-elevation-1));

  &__notice {
    font-size: var(--font-size-md);
    line-height: 1.4;
    color: rgba(var(--v-theme-text-primary));
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
